<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData
            :coaching="coachingData"
            :calculatedUnits="calculatedUnitsData"/>

        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
          <div class="row mb-2 mt-4">
            <div class="col-lg-2 col-md-3 col-sm-3">
              Vorzeitige Beendigung?
            </div>
            <div class="col-lg-2 col-md-3 col-sm-3">
              <BaseRadio
                  v-model="formData.prematureTermination"
                  label="Ja"
                  name="prematureTermination"
                  id="1"
                  :labelPosition="'right'"
                  classFormGroup="form-check form-check-inline"
                  :validation="v$.prematureTermination"
                  :messages="v$.prematureTermination.required.$message"/>
              <BaseRadio
                  v-model="formData.prematureTermination"
                  label="Nein"
                  name="prematureTermination"
                  id="0"
                  :labelPosition="'right'"
                  classFormGroup="form-check form-check-inline"
                  :validation="v$.prematureTermination"
                  :messages="v$.prematureTermination.required.$message"/>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-2 col-md-4 col-sm-12">
              Beendigungsdatum:
            </div>
            <div class="col-lg-2 col-md-4 col-sm-12">
              <Datepicker
                  v-model="formData.terminationDate"
                  position="down"
                  :validation="v$.terminationDate"
                  :messages="v$.terminationDate.required.$message"/>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-lg-2 col-md-4 col-sm-12">
              Grund der Beendigung:
            </div>
            <div class="col-lg-5 col-md-12 col-sm-12">
              <SelectVue v-model="formData.reasonForTermination"
                         :options="reasonsForTermination || []"
                         placeholder="Wert auswählen"
                         :validation="v$.reasonForTermination"
                         :messages="v$.reasonForTermination.required.$message"/>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-lg-12">
              <TextEditor
                  v-model="formData.targetControl"
                  @update:model-value="formData.targetControl = $event"
                  :inputStyle="{ height: '200px' }"
                  label="Zielkontrolle"/>
            </div>
            <div class="col-lg-12 mt-4">
              <TextEditor
                  v-model="formData.recommendedAction"
                  @update:model-value="formData.recommendedAction = $event"
                  :inputStyle="{ height: '200px' }"
                  label="Handlungsempfehlung"/>
            </div>
          </div>

          <ValidationErrorMessage :validationErrors=v$?.$errors></ValidationErrorMessage>

          <div class="d-flex justify-content-center w-100 mt-4">
            <SaveButton
                :label="$t('general.button.save')"
                :loading="isSubmitting"/>
          </div>
        </form>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import portalAssistedLivingCoachingService from '@/api/Portal/AssistedLivingCoaching';
import assistedLivingCoachingService from '@/api/Unemployed/AssistedLivingCoaching';
import BaseRadio from '@/components/base/BaseRadio.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import TextEditor from '@/components/TextEditor.vue';
import ValidationErrorMessage from '@/components/ValidationErrorMessage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/AssistedLivingCoaching/CoachingOverviewData.vue';
import Menu from '@/views/App/Portal/AssistedLivingCoaching/Menu.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }                     = useI18n();
const piniaEnumsState           = piniaEnumsStore();
const { reasonsForTermination } = computed(() => piniaEnumsState.enums).value;
const piniaUserState            = piniaUserStore();
const piniaCurrentUser          = computed(() => piniaUserState.currentUser.user);
const route                     = useRoute();
const coachingData              = ref('');
const calculatedUnitsData       = ref('');
const isLoaded                  = ref(false);
const isSubmitting              = ref(false);
const coachingId                = +route.params.id;

const formData = ref({
  coachingId: '',
  prematureTermination: '0',
  terminationDate: '',
  reasonForTermination: '',
  targetControl: '',
  recommendedAction: '',
});

const formRules = ref({
  prematureTermination: requiredRule(t),
  terminationDate: requiredRule(t),
  reasonForTermination: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {

  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.coachingId = +coachingId;

    await portalAssistedLivingCoachingService.submitFinalInterview(formData.value);

    await getFinalInterviewData();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getCoachingData() {
  try {
    CloseToast();
    const { data } = await assistedLivingCoachingService.getAssistedLivingCoaching(+coachingId);

    coachingData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getFinalInterviewData() {
  try {
    const { data }           = await portalAssistedLivingCoachingService.getFinalInterview(+coachingId);
    const finalInterviewData = data?.data;
    if (finalInterviewData) {
      formData.value.prematureTermination = String(finalInterviewData?.prematureTermination ?? '');
      formData.value.terminationDate      = finalInterviewData?.terminationDate;
      formData.value.reasonForTermination = finalInterviewData?.reasonForTermination;
      formData.value.targetControl        = finalInterviewData?.targetControl;
      formData.value.recommendedAction    = finalInterviewData?.recommendedAction;
    }

    const { data: calculatedUnits } = await assistedLivingCoachingService.getCalculatedUnits(+coachingId);
    calculatedUnitsData.value       = calculatedUnits.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getCoachingData(),
    getFinalInterviewData(),
  ]);

  isLoaded.value = true;
});
</script>
<style scoped>

</style>